import {
  ComboFlightSearchResponse,
  FlightSearchResponse,
  MultiCityFlightSearchResponse,
  OneWayFlightSearchResponse,
  RoundTripFlightSearchResponse,
} from "@/types/search/flight";

export interface GetFlightDataOptions {
  matchedFlights?: Array<{ flight: { id: string }; tripType: string; isPerfectMatch?: boolean; isExactMatch?: boolean; matchScore?: number }>;
  manualBlockId?: string | null;
}

const isRoundTrip = (
  response: FlightSearchResponse,
): response is RoundTripFlightSearchResponse => {
  return "RETURN" in response.searchResult.tripInfos;
};

const isOneWay = (
  response: FlightSearchResponse,
): response is OneWayFlightSearchResponse => {
  return (
    "ONWARD" in response.searchResult.tripInfos &&
    !("RETURN" in response.searchResult.tripInfos)
  );
};

const isMultiCity = (
  response: FlightSearchResponse,
): response is MultiCityFlightSearchResponse => {
  return (
    !("ONWARD" in response.searchResult.tripInfos) &&
    !("RETURN" in response.searchResult.tripInfos) &&
    !("COMBO" in response.searchResult.tripInfos)
  );
};

const isCombo = (
  response: FlightSearchResponse,
): response is ComboFlightSearchResponse => {
  return "COMBO" in response.searchResult.tripInfos;
};

export function getFlightData(
  filteredFlights: FlightSearchResponse | null | undefined,
  options: GetFlightDataOptions = {},
) {
  const { matchedFlights = [], manualBlockId } = options;

  if (!filteredFlights?.searchResult) {
    return {
      onward: [],
      returnFlight: undefined,
      multiCity: {},
      combo: undefined,
    };
  }

  const ensureFlightIdsAndSort = (flights: any[], tripKey: string = "ONWARD") => {
    const flightsWithIds = flights?.map((flight, index) => {
      if (!flight.id) {
        const flightId =
          flight.totalPriceList?.[0]?.id ||
          `${tripKey}-${index}-${flight.sI?.map((s: any) => s.id).join("-") || "no-segments"}`;
        return { ...flight, id: flightId };
      }
      return flight;
    });

    if (!manualBlockId || !matchedFlights.length) {
      return flightsWithIds;
    }

    return flightsWithIds.sort((a, b) => {
      const aMatch = matchedFlights.find(
        (m) => m.flight.id === a.id && m.tripType === tripKey,
      );
      const bMatch = matchedFlights.find(
        (m) => m.flight.id === b.id && m.tripType === tripKey,
      );

      if (aMatch?.isPerfectMatch && !bMatch?.isPerfectMatch) return -1;
      if (!aMatch?.isPerfectMatch && bMatch?.isPerfectMatch) return 1;
      if (aMatch?.isExactMatch && !bMatch?.isExactMatch) return -1;
      if (!aMatch?.isExactMatch && bMatch?.isExactMatch) return 1;
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      if (aMatch && bMatch) {
        return (bMatch.matchScore ?? 0) - (aMatch.matchScore ?? 0);
      }
      return 0;
    });
  };

  if (isRoundTrip(filteredFlights)) {
    const tripInfos = filteredFlights.searchResult.tripInfos;
    return {
      onward: ensureFlightIdsAndSort(tripInfos.ONWARD || [], "ONWARD"),
      returnFlight: ensureFlightIdsAndSort(tripInfos.RETURN || [], "RETURN"),
      multiCity: {},
      combo: undefined,
    };
  }

  if (isOneWay(filteredFlights)) {
    return {
      onward: ensureFlightIdsAndSort(
        filteredFlights.searchResult.tripInfos.ONWARD,
        "ONWARD",
      ),
      returnFlight: undefined,
      multiCity: {},
      combo: undefined,
    };
  }

  if (isCombo(filteredFlights)) {
    return {
      onward: undefined,
      returnFlight: undefined,
      multiCity: undefined,
      combo: ensureFlightIdsAndSort(
        filteredFlights.searchResult.tripInfos.COMBO,
        "COMBO",
      ),
    };
  }

  if (isMultiCity(filteredFlights)) {
    const sortedMultiCity: Record<string, any[]> = {};
    Object.keys(filteredFlights.searchResult.tripInfos).forEach((key) => {
      sortedMultiCity[key] = ensureFlightIdsAndSort(
        filteredFlights.searchResult.tripInfos[key],
        key,
      );
    });

    return {
      onward: [],
      returnFlight: undefined,
      multiCity: sortedMultiCity,
      combo: undefined,
    };
  }

  return {
    onward: [],
    returnFlight: undefined,
    multiCity: {},
    combo: undefined,
  };
}
